import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Plus, Search, Edit, Trash2, Loader2, X, Shield, UserCog, ShieldCheck } from 'lucide-react'
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api'

type Rol = 'admin' | 'operator'

interface AdminRow {
  id: string
  email: string
  nombre: string
  rol: Rol
  activo: boolean
  creadoEn: string | null
  actualizadoEn: string | null
}

interface AdminsData {
  total: number
  page: number
  pageSize: number
  pages: number
  data: AdminRow[]
}

interface FormState {
  email: string
  nombre: string
  password: string
  rol: Rol
  activo: boolean
}

const EMPTY_FORM: FormState = {
  email: '',
  nombre: '',
  password: '',
  rol: 'operator',
  activo: true,
}

interface CustomRole {
  id: number
  name: string
  description: string
  is_system: boolean
  permissions: number[]
  user_count: number
}
interface RolesResp {
  data: CustomRole[]
}
interface UserRolesResp {
  data: number[]
}

function rolBadge(rol: Rol) {
  if (rol === 'admin') {
    return (
      <Badge variant="danger">
        <Shield className="size-3" /> Admin
      </Badge>
    )
  }
  return (
    <Badge variant="info">
      <UserCog className="size-3" /> Operador
    </Badge>
  )
}

function fmtDate(d: string | null): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

export function Admins() {
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [formError, setFormError] = useState('')
  const [selectedRoles, setSelectedRoles] = useState<Set<number>>(new Set())

  // Catálogo de roles personalizados (admin_roles)
  const { data: customRoles } = useQuery<RolesResp>({
    queryKey: ['admin-roles'],
    queryFn: () => apiGet('/api/admin/roles'),
  })

  // Roles asignados al usuario que se está editando
  const { data: userRolesData } = useQuery<UserRolesResp>({
    queryKey: ['admin-user-roles', editingId],
    queryFn: () => apiGet(`/api/admin/usuarios/${editingId}/roles`),
    enabled: !!editingId,
  })

  useEffect(() => {
    if (editingId && userRolesData) setSelectedRoles(new Set(userRolesData.data))
  }, [editingId, userRolesData])

  const assignRolesMut = useMutation({
    mutationFn: (args: { userId: string; roleIds: number[] }) =>
      apiPost(`/api/admin/usuarios/${args.userId}/roles`, { role_ids: args.roleIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-roles'] })
      qc.invalidateQueries({ queryKey: ['admin-user-roles'] })
    },
  })

  const query = new URLSearchParams()
  query.set('page', String(page))
  if (searchTerm) query.set('q', searchTerm)

  const { data, isLoading, isFetching } = useQuery<AdminsData>({
    queryKey: ['admins', page, searchTerm],
    queryFn: () => apiGet(`/api/admin/usuarios?${query.toString()}`),
    placeholderData: (prev) => prev,
  })

  const createMut = useMutation({
    mutationFn: async (payload: { email: string; name: string; password: string; role: Rol }) => {
      const created = (await apiPost<{ ok: boolean; data: { id: string } }>(
        '/api/admin/usuarios',
        payload,
      )) as { ok: boolean; data: { id: string } }
      if (selectedRoles.size > 0 && created.data?.id) {
        await apiPost(`/api/admin/usuarios/${created.data.id}/roles`, {
          role_ids: Array.from(selectedRoles),
        })
      }
      return created
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admins'] })
      qc.invalidateQueries({ queryKey: ['admin-roles'] })
      closeModal()
    },
    onError: (e: Error) => setFormError(e.message),
  })

  const updateMut = useMutation({
    mutationFn: async (args: { id: string; body: Record<string, unknown> }) => {
      const updated = await apiPut(`/api/admin/usuarios/${args.id}`, args.body)
      await assignRolesMut.mutateAsync({
        userId: args.id,
        roleIds: Array.from(selectedRoles),
      })
      return updated
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admins'] })
      qc.invalidateQueries({ queryKey: ['admin-roles'] })
      closeModal()
    },
    onError: (e: Error) => setFormError(e.message),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/admin/usuarios/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admins'] }),
    onError: (e: Error) => alert(e.message),
  })

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearchTerm(q.trim())
  }

  function openNew() {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setFormError('')
    setSelectedRoles(new Set())
    setModalOpen(true)
  }

  function openEdit(row: AdminRow) {
    setEditingId(row.id)
    setForm({
      email: row.email,
      nombre: row.nombre,
      password: '',
      rol: row.rol,
      activo: row.activo,
    })
    setFormError('')
    setSelectedRoles(new Set())
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditingId(null)
    setForm(EMPTY_FORM)
    setFormError('')
    setSelectedRoles(new Set())
  }

  function toggleRole(id: number) {
    setSelectedRoles((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function onDelete(row: AdminRow) {
    if (
      window.confirm(
        `¿Eliminar usuario "${row.nombre || row.email}"?\nSe marcará como inactivo.`,
      )
    ) {
      deleteMut.mutate(row.id)
    }
  }

  function submitForm(e: React.FormEvent) {
    e.preventDefault()
    setFormError('')

    if (!form.email.trim()) return setFormError('Email es requerido')
    if (!form.nombre.trim()) return setFormError('Nombre es requerido')

    if (editingId) {
      const body: Record<string, unknown> = {
        email: form.email.trim(),
        name: form.nombre.trim(),
        role: form.rol,
        is_active: form.activo,
      }
      if (form.password.trim()) {
        if (form.password.length < 6) {
          return setFormError('La contraseña debe tener al menos 6 caracteres')
        }
        body.password = form.password
      }
      updateMut.mutate({ id: editingId, body })
    } else {
      if (!form.password.trim()) return setFormError('Contraseña es requerida')
      if (form.password.length < 6) {
        return setFormError('La contraseña debe tener al menos 6 caracteres')
      }
      createMut.mutate({
        email: form.email.trim(),
        name: form.nombre.trim(),
        password: form.password,
        role: form.rol,
      })
    }
  }

  const isSaving = createMut.isPending || updateMut.isPending

  return (
    <>
      <Topbar
        title="Admins y operadores"
        subtitle={data ? `${data.total.toLocaleString('es-MX')} usuarios del panel` : 'Cargando…'}
        actions={
          <button
            type="button"
            onClick={openNew}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="size-3.5" /> Nuevo admin
          </button>
        }
      />
      <div className="p-6">
        <form onSubmit={onSearch} className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre o email…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Buscar
          </button>
        </form>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando usuarios…</p>
            </div>
          )}

          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Nombre</th>
                      <th className="px-4 py-3 font-medium">Rol</th>
                      <th className="px-4 py-3 font-medium">Activo</th>
                      <th className="px-4 py-3 font-medium">Última conexión</th>
                      <th className="w-24 px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-sm text-ink-500">
                          Sin resultados
                        </td>
                      </tr>
                    )}
                    {data.data.map((u) => (
                      <tr key={u.id} className="border-t border-ink-100 hover:bg-ink-50">
                        <td className="px-4 py-3 font-mono text-xs text-ink-700">{u.email}</td>
                        <td className="px-4 py-3 font-medium text-ink-900">
                          {u.nombre || '(sin nombre)'}
                        </td>
                        <td className="px-4 py-3">{rolBadge(u.rol)}</td>
                        <td className="px-4 py-3">
                          {u.activo ? (
                            <Badge variant="success">Activo</Badge>
                          ) : (
                            <Badge variant="default">Inactivo</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-ink-500">
                          {fmtDate(u.actualizadoEn)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => openEdit(u)}
                              className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                              title="Editar"
                            >
                              <Edit className="size-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => onDelete(u)}
                              disabled={deleteMut.isPending}
                              className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700 disabled:opacity-50"
                              title="Eliminar"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                <div className="text-ink-500">
                  Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                  <strong className="text-ink-900">{data.pages.toLocaleString('es-MX')}</strong> (
                  {data.total.toLocaleString('es-MX')} usuarios)
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button className="rounded-md bg-brand-600 px-3 py-1 text-white">{page}</button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pages}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-ink-200 px-5 py-3">
              <h2 className="text-base font-semibold text-ink-900">
                {editingId ? 'Editar usuario' : 'Nuevo usuario'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-md p-1 text-ink-500 hover:bg-ink-100"
              >
                <X className="size-4" />
              </button>
            </div>
            <form onSubmit={submitForm} className="space-y-3 px-5 py-4">
              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-700">Email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-700">Nombre</label>
                <input
                  type="text"
                  required
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-700">
                  Contraseña{' '}
                  {editingId && (
                    <span className="font-normal text-ink-500">
                      (dejá vacío para no cambiar)
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  required={!editingId}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editingId ? '••••••••' : 'Mínimo 6 caracteres'}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-700">Rol base</label>
                <select
                  value={form.rol}
                  onChange={(e) => setForm({ ...form, rol: e.target.value as Rol })}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                >
                  <option value="operator">Operador</option>
                  <option value="admin">Administrador</option>
                </select>
                <p className="mt-1 text-[11px] text-ink-500">
                  Admin = bypass de todos los permisos. Operator = solo los permisos de los roles
                  personalizados.
                </p>
              </div>

              <div>
                <label className="mb-1 flex items-center gap-2 text-xs font-semibold text-ink-700">
                  <ShieldCheck className="size-3.5" />
                  Roles personalizados{' '}
                  <span className="font-normal text-ink-500">
                    ({selectedRoles.size} seleccionados)
                  </span>
                </label>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-ink-200 bg-white p-2">
                  {!customRoles && (
                    <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-ink-500">
                      <Loader2 className="size-3 animate-spin" /> Cargando roles…
                    </div>
                  )}
                  {customRoles && customRoles.data.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-ink-500">
                      No hay roles configurados. Creá uno desde "Roles y permisos".
                    </div>
                  )}
                  {customRoles?.data.map((r) => {
                    const on = selectedRoles.has(r.id)
                    return (
                      <label
                        key={r.id}
                        className={`flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
                          on ? 'bg-brand-50' : 'hover:bg-ink-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleRole(r.id)}
                          className="mt-0.5 size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                        />
                        <span className="flex-1">
                          <span className="block font-mono text-xs font-semibold text-ink-900">
                            {r.name}
                            {r.is_system && (
                              <span className="ml-1 rounded bg-blue-100 px-1 text-[10px] text-blue-700">
                                sistema
                              </span>
                            )}
                          </span>
                          {r.description && (
                            <span className="block text-[11px] text-ink-500">{r.description}</span>
                          )}
                          <span className="block text-[10px] text-ink-400">
                            {r.permissions.length} permisos
                          </span>
                        </span>
                      </label>
                    )
                  })}
                </div>
              </div>
              {editingId && (
                <label className="flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={form.activo}
                    onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                  />
                  Activo
                </label>
              )}

              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <div className="flex justify-end gap-2 border-t border-ink-100 pt-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
                >
                  {isSaving && <Loader2 className="size-4 animate-spin" />}
                  {editingId ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

export default Admins
